import React, { FC } from 'react';
import { QueryClient } from '@tanstack/react-query';
import type { Request } from 'express';

import { RoverLoading } from '@rover/react-lib/src/components';
import getQueryError from '@rover/react-lib/src/utils/getQueryError';
import { HttpError } from '@rover/rsdk/src/errors';
import getPath from '@rover/shared/js/utils/getPath';
import { Http404Error, Http410Error, Http500Error } from '@rover/utilities/HttpError';

import InternalLinkingPage from './InternalLinkingPage';
import { pathToLocality } from './locality';
import { getInternalLinkingPrefetchQueryOptions, useInternalLinking } from './useInternalLinking';

const InternalLinkingPageContainer: FC & { reactQueryPrefetch: any } = () => {
  const path = `${getPath()}/`;
  const locality = pathToLocality(path);
  const { data, isLoading, isError, error } = useInternalLinking(locality);

  if (isError || !data) {
    if (error instanceof HttpError && error?.statusCode === 404) {
      throw new Http404Error();
    }
    if (error instanceof HttpError && error?.statusCode === 410) {
      throw new Http410Error();
    }
    throw new Http500Error();
  }

  if (isLoading) {
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  return <InternalLinkingPage {...data.results} />;
};

InternalLinkingPageContainer.reactQueryPrefetch = async (
  queryClient: QueryClient,
  req: Request
) => {
  const path = `${req.path}/`;
  const locality = pathToLocality(path);

  await queryClient.fetchQuery(getInternalLinkingPrefetchQueryOptions(locality)).catch((error) => {
    throw getQueryError(error) ?? error;
  });
};

export default InternalLinkingPageContainer;
